<template>
  <div>
    <Survey v-if="survey" :survey="survey" />
    <div v-else class="loading-message">Loading survey...</div>

    <!-- Prevent "blank" moments between completing the current form and navigating to the next task -->
    <div v-if="isTransitioning" class="task-transition-overlay" aria-live="polite">
      <div class="task-transition-card">
        <div class="task-transition-title">{{ transitionMessage }}</div>
      </div>
    </div>
  </div>
</template>

<script>
import 'survey-core/defaultV2.min.css'
import api from '../../api/http-common.js'
import { mapGetters, mapActions } from 'vuex'
import { Survey } from 'survey-vue-ui'
import { Model, slk } from 'survey-core'
import { SurveyPDF, HTMLBrick } from 'survey-pdf'
import moment from 'moment'

slk(
  'ZjI3MDkwMDYtNDkxNC00MmJkLTg4NDUtMjFhYjIyMDM5MWQxOzE9MjAyNi0wMy0wNCwyPTIwMjYtMDMtMDQsND0yMDI2LTAzLTA0'
)

export default {
  data() {
    return {
      survey: null,
      surveyJson: null,
      patientName: '',
      pdfFilename: '',
      pdfDocOptions: {
        compress: true,
        fontSize: 12,
        htmlRenderAs: 'image' // or "image" | "auto"
      },
      isLoading: true,
      isTransitioning: false,
      transitionMessage: 'Submitting your form…'
    }
  },
  components: {
    Survey
  },
  computed: {
    ...mapGetters(['currentPatientAssociated'])
  },
  methods: {
    ...mapActions([
      'setTaskCurrentTaskType',
      'clearUrlLinkInfo',
      'setTaskCode',
      'setTaskInfo',
      'setTaskItem',
      'setCurrentMsgInfo',
      'setMsgCurrentPage',
      'setBillingStatementTask'
    ]),
    alertResults(sender) {
      // const results = JSON.stringify(sender.data)
      setTimeout(() => {
        window.opener.location.reload()
        window.close()
      }, 3000)
    },
    async complete() {
      // Show an overlay immediately so users don't see a confusing flash of the survey completion screen
      this.isTransitioning = true
      this.transitionMessage = 'Submitting your form…'

      const queryData = this.$route.query
      // const jData = JSON.stringify(this.surveyJson)
      const options = {
        fontSize: 14,
        compress: true,
        format: 'a3',
        htmlRenderAs: 'image',
        orientation: 'p',
        wrapText: true
      }

      // ----- ADDED 03/28/2025
      var el = this.surveyJson.pages[0].elements

      var htmlElements = el.filter((element) => element.type === 'html')
      const count = htmlElements.length

      if (count > 1) {
        el.forEach((element) => {
          if (element.type === 'html') {
            element.html += '<br>' // Add page break
          }
        })
      } else if (count === 1) {
        el.forEach((element) => {
          if (element.type === 'html') {
            const fitsOnPage = this.checkHtmlHeight(element.html)
            if (!fitsOnPage) {
              element.html += '<br><br>' // Add page break
            } else {
              element.html = `<div style='margin-bottom: 20px; margin-top:20px;'>${element.html}</div><br>`
            }
          }
        })
      }
      this.surveyJson.pages[0].elements = el
      // ----- END 03/28/2025

      const surveyPdf = new SurveyPDF(this.surveyJson, options)
      if (surveyPdf) {
        surveyPdf.data = this.survey.data
        surveyPdf.onRenderQuestion.add(async (survey, options) => {
          // change the css of checkbox
          if (options.question.getType() === 'checkbox') {
            for (var i = 0; i < options.bricks.length; i++) {
              var unfoldBricks = options.bricks[i].unfold()
              for (var j = 0; j < unfoldBricks.length; j++) {
                if (
                  unfoldBricks[j] instanceof options.module.CheckboxItemBrick
                ) {
                  if (unfoldBricks[j].context.checked) {
                    unfoldBricks[j].formBorderColor = 'black'
                  }
                  unfoldBricks[j].textColor = 'black'
                }
              }
            }
          }

          // Split only HTML elements
          // ----- START COMMENTED OUT 03/28/2025
          // if (options.question.getType() === 'html') {
          //   // Get the HTML brick
          //   const htmlBrick = options.bricks[0]
          //   if (!htmlBrick || !htmlBrick.html) return

          //   options.bricks = []

          //   // Extract image from the HTML brick
          //   const img = await this.getImage(htmlBrick)

          //   // Calculate the available page height and the coordinate at which to start drawing
          //   const fullPageHeight = options.controller.paperHeight - options.controller.margins.top - options.controller.margins.bot
          //   const currY = htmlBrick.yTop - fullPageHeight * Math.floor(htmlBrick.yTop / fullPageHeight)

          //   const canvas = document.createElement('canvas')
          //   canvas.width = img.width

          //   // A multiplier is used to decrease the original image width if it exceeds the available page width
          //   let multiplier = 1
          //   if (htmlBrick.width < img.width) {
          //     multiplier = htmlBrick.width / img.width
          //   }

          //   // Calculate what portion of the original image would fit onto one page
          //   const onePageSize = fullPageHeight / multiplier
          //   // Calculate the height of the first brick
          //   const firstBrickHeight = Math.min(img.height, (fullPageHeight - currY) / multiplier)

          //   // Draw the first brick
          //   const firstBrick = this.drawPart(firstBrickHeight, multiplier, 0, canvas, img,
          //     0, 0, onePageSize, htmlBrick, options.controller)
          //   options.bricks.push(firstBrick)

          //   // Page counter
          //   let cnt = 0
          //   // Draw the rest of the bricks
          //   for (let i = firstBrickHeight; i < img.height; i += onePageSize) {
          //     const brickHeight = Math.min(img.height - i, onePageSize)

          //     const currBrick = this.drawPart(brickHeight, multiplier, i, canvas, img,
          //       firstBrickHeight, cnt, onePageSize, htmlBrick, options.controller)

          //     cnt += 1
          //     options.bricks.push(currBrick)
          //   }
          // }
          // ----- END COMMENTED OUT 03/28/2025
        })

        // insert patient form before changing the date format
        var patientFormID = await api.task.request.insertPatientForm({
          facilityformId: queryData.i,
          patientid: queryData.pid,
          data: JSON.stringify(surveyPdf.data)
        })

        // change date format
        surveyPdf.getAllQuestions().forEach((q) => {
          if (q.getType() === 'text' && q.inputType === 'date') {
            if (!q.isEmpty()) {
              // const d = new Date(q.value)
              // const newVal = (d.getMonth() + 1) + '-' + d.getDate() + '-' + d.getFullYear()
              // q.value = newVal
              const newVal = moment(q.value).format('MM/DD/YYYY')
              q.value = newVal
            }
          }
        })

        surveyPdf.raw('dataurlstring').then(async (dataurlstring) => {
          var base64 = dataurlstring.split('base64,')[1]

          if (patientFormID) {
            if (patientFormID.data > 0) {
              const retStat = await api.task.request.uploadPatientForm({
                facilityId: this.currentPatientAssociated.facilityID,
                patientId: queryData.pid,
                description: this.surveyJson.title,
                base64: base64,
                facilityFormID: queryData.i,
                patientFormID: patientFormID.data
              })

              if (retStat.data) {
                const retStat2 = await api.task.request.updateTaskComplete({
                  facilityId: this.currentPatientAssociated.facilityID,
                  id: queryData.tid,
                  patientId: queryData.pid
                })

                if (!retStat2.data) {
                  // delete
                  await api.task.request.deletePatientForm(patientFormID.data)
                  this.isTransitioning = false
                } else {
                  // Task completed successfully; check for more FORM tasks before redirecting
                  await new Promise(resolve => setTimeout(resolve, 500))
                  await this.checkAndProcessRemainingTasks(queryData)
                }
              } else {
                // delete
                await api.task.request.deletePatientForm(patientFormID.data)
                this.isTransitioning = false
              }
            }
          }
        })
      }
    },
    async loadData() {
      const queryData = this.$route.query
      if (queryData) {
        try {
          var res = await api.task.request.getFacilityFormJsonQueryObj({
            facilityFormId: queryData.i,
            patientId: queryData.pid
          })

          if (!res || !res.data) {
            console.error('Invalid response from API')
            return null
          }

          var objData = JSON.parse(res.data.data)
          var el = objData.pages[0].elements

          var itemIndex = el.findIndex((x) => x.name === 'patientName')
          if (itemIndex) {
            el[itemIndex].defaultValue = queryData.p
          }
          this.surveyJson = objData
          this.pdfFilename = objData.title
          // Don't show a "Thank you" completion screen immediately on submit because we may
          // still have more tasks/forms to complete. We'll update this message only if the
          // re-fetch confirms there are no remaining tasks.
          objData.completedHtml =
            '<span style="font-size: 20px; font-weight: 600">Submitting your form…</span>'
          console.log(objData)
          return objData
        } catch (error) {
          console.error('Error loading form data:', error)
          return null
        }
      } else {
        return null
      }
    },
    savePdf() {
      const surveyPdf = new SurveyPDF(this.surveyJson, this.pdfDocOptions)
      if (surveyPdf) {
        surveyPdf.data = this.survey.data
        surveyPdf.save(this.pdfFilename)
      }
    },
    savePdf2(surveyModel) {
      const options = {
        fontSize: 14,
        compress: true,
        format: 'a3',
        htmlRenderAs: 'image',
        orientation: 'p',
        wrapText: true
      }

      //
      var el = this.surveyJson.pages[0].elements

      var htmlElements = el.filter((element) => element.type === 'html')
      const count = htmlElements.length

      if (count > 1) {
        el.forEach((element) => {
          if (element.type === 'html') {
            element.html += '<br>' // Add page break
          }
        })
      } else if (count === 1) {
        el.forEach((element) => {
          if (element.type === 'html') {
            const fitsOnPage = this.checkHtmlHeight(element.html)
            if (!fitsOnPage) {
              element.html += '<br><br>' // Add page break
            } else {
              element.html = `<div style='margin-bottom: 20px; margin-top:20px;'>${element.html}</div><br>`
            }
          }
        })
      }
      this.surveyJson.pages[0].elements = el

      const surveyPDF = new SurveyPDF(this.surveyJson, options)
      if (surveyModel) {
        surveyPDF.data = surveyModel.data
        // add date format
        surveyPDF.getAllQuestions().forEach((q) => {
          if (q.getType() === 'text' && q.inputType === 'date') {
            if (!q.isEmpty()) {
              const newVal = moment(q.value).format('MM/DD/YYYY')
              q.value = newVal
            }
          }
        })
      }
      surveyPDF.onRenderQuestion.add(async (survey, options) => {
        // change the css of checkbox
        if (options.question.getType() === 'checkbox') {
          for (var i = 0; i < options.bricks.length; i++) {
            var unfoldBricks = options.bricks[i].unfold()
            for (var j = 0; j < unfoldBricks.length; j++) {
              if (unfoldBricks[j] instanceof options.module.CheckboxItemBrick) {
                if (unfoldBricks[j].context.checked) {
                  unfoldBricks[j].formBorderColor = 'black'
                }
                unfoldBricks[j].textColor = 'black'
              }
            }
          }
        }
        // Split only HTML elements
        // if (options.question.getType() === 'html') {
        //   // Get the HTML brick
        //   const htmlBrick = options.bricks[0]
        //   if (!htmlBrick || !htmlBrick.html) return

        //   options.bricks = []

        //   // Extract image from the HTML brick
        //   const img = await this.getImage(htmlBrick)

        //   // Calculate the available page height and the coordinate at which to start drawing
        //   const fullPageHeight = options.controller.paperHeight - options.controller.margins.top - options.controller.margins.bot
        //   const currY = htmlBrick.yTop - fullPageHeight * Math.floor(htmlBrick.yTop / fullPageHeight)

        //   const canvas = document.createElement('canvas')
        //   canvas.width = img.width

        //   // A multiplier is used to decrease the original image width if it exceeds the available page width
        //   let multiplier = 1
        //   if (htmlBrick.width < img.width) {
        //     multiplier = htmlBrick.width / img.width
        //   }

        //   // Calculate what portion of the original image would fit onto one page
        //   const onePageSize = fullPageHeight / multiplier
        //   // Calculate the height of the first brick
        //   const firstBrickHeight = Math.min(img.height, (fullPageHeight - currY) / multiplier)

        //   // Draw the first brick
        //   const firstBrick = this.drawPart(firstBrickHeight, multiplier, 0, canvas, img,
        //     0, 0, onePageSize, htmlBrick, options.controller)
        //   options.bricks.push(firstBrick)

        //   // Page counter
        //   let cnt = 0
        //   // Draw the rest of the bricks
        //   for (let i = firstBrickHeight; i < img.height; i += onePageSize) {
        //     const brickHeight = Math.min(img.height - i, onePageSize)

        //     const currBrick = this.drawPart(brickHeight, multiplier, i, canvas, img,
        //       firstBrickHeight, cnt, onePageSize, htmlBrick, options.controller)

        //     cnt += 1
        //     options.bricks.push(currBrick)
        //   }
        // }
      })
      try {
        surveyPDF.save(this.pdfFilename)
      } catch (e) {
        console.log(e)
      }
    },
    // Draw a portion of an image
    drawPart(
      brickHeight,
      multiplier,
      shift,
      canvas,
      img,
      firstBrickHeight,
      fullPagesCnt,
      onePageSize,
      htmlBrick,
      controller
    ) {
      canvas.height = brickHeight
      const ctx = canvas.getContext('2d')
      ctx.clearRect(0, 0, canvas.width, canvas.height)
      ctx.drawImage(
        img,
        0,
        shift,
        img.width,
        brickHeight,
        0,
        0,
        img.width,
        brickHeight
      )

      const currImageBase64 = canvas.toDataURL('image/jpeg')
      const brickHtml = `
                <img src='${currImageBase64}' width='${
        img.width * multiplier
      }' height='${brickHeight * multiplier}' />`
      const brickRect = {
        xLeft: htmlBrick.xLeft,
        xRight: htmlBrick.xRight,
        yTop:
          htmlBrick.yTop +
          multiplier * (firstBrickHeight + fullPagesCnt * onePageSize),
        yBot:
          htmlBrick.yTop +
          multiplier *
            (firstBrickHeight + fullPagesCnt * onePageSize + brickHeight)
      }
      return new HTMLBrick(null, controller, brickRect, brickHtml, true)
    },
    async getImage(htmlBrick) {
      const html = htmlBrick.html
      let start = html.indexOf('data')

      let imageBase64 = ''
      while (html[start] !== "'") {
        imageBase64 += html[start]
        start++
      }
      const img = new Image()
      img.src = imageBase64
      await new Promise((resolve, reject) => {
        img.onload = () => resolve(img)
        img.onerror = reject
      })

      return img
    },
    // Function to check if HTML content fits on A3 page
    checkHtmlHeight(html) {
      const pageHeight = 1169 // A3 page height in pixels
      const div = document.createElement('div')
      div.innerHTML = html
      document.body.appendChild(div)
      const height = div.offsetHeight
      document.body.removeChild(div)
      return height <= pageHeight
    },
    async processNextTask(task, queryData) {
      let taskRes
      const isMobileDevice = window.navigator.userAgent
        .toLowerCase()
        .includes('mobi')
      const isPopup = !isMobileDevice && window.opener

      this.setTaskCode(task.taskType)
      switch (task.taskType) {
        case 'NPH':
        case 'AANPH':
        case 'INS':
        case 'AACH':
          await this.clearUrlLinkInfo()
          if (task.info !== '') {
            var taskinfo = JSON.parse(task.info)
            taskinfo.TaskType = task.taskType
            taskinfo.TaskID = task.taskID
            this.setTaskInfo(JSON.stringify(taskinfo))
          } else {
            this.setTaskInfo('')
          }
          this.setTaskCurrentTaskType('PatientInfo')
          if (isPopup) {
            // For popup, redirect opener window
            window.opener.location.href = '/task'
            setTimeout(() => {
              window.close()
            }, 500)
          } else {
            await this.$router.push({ path: '/task' })
          }
          break
        case 'CH':
          await this.clearUrlLinkInfo()
          this.setTaskInfo(task.info)
          this.setTaskItem(task)
          this.setTaskCurrentTaskType('PatientInfo')
          if (isPopup) {
            window.opener.location.href = '/task'
            setTimeout(() => {
              window.close()
            }, 500)
          } else {
            await this.$router.push({ path: '/task' })
          }
          break
        case 'MSG':
          taskRes = await api.task.request.getSpecificTask(task.taskID)
          await api.task.request.updateTaskComplete({
            facilityId: this.currentPatientAssociated.facilityID,
            id: task.taskID,
            patientId: this.currentPatientAssociated.patientID
          })
          await this.clearUrlLinkInfo()
          this.setCurrentMsgInfo({
            id: parseInt(taskRes.data.info),
            subject: ''
          })
          this.setMsgCurrentPage('Detail')
          if (isPopup) {
            window.opener.location.href = '/menu/messages'
            setTimeout(() => {
              window.close()
            }, 500)
          } else {
            this.$router.push({ path: '/menu/messages' })
          }
          break
        case 'STMT':
          taskRes = await api.task.request.getSpecificTask(task.taskID)
          this.setBillingStatementTask(taskRes.data)
          await this.clearUrlLinkInfo()
          if (isPopup) {
            window.opener.location.href = '/menu/statement'
            setTimeout(() => {
              window.close()
            }, 500)
          } else {
            this.$router.push({ path: '/menu/statement' })
          }
          break
        case 'FORM':
          await this.clearUrlLinkInfo()
          var info = JSON.parse(task.info)
          var routeData = this.$router.resolve({
            name: 'HandlerForm',
            query: {
              i: info.FacilityFormID,
              t: info.FormName,
              p: info.Data.PatientName,
              pid: task.patientID,
              tid: task.taskID
            }
          })
          // For FORM tasks, open in same window/popup
          if (isMobileDevice) {
            window.location.href = routeData.href
          } else if (isPopup) {
            // Open next form in same popup
            window.location.href = routeData.href
          } else {
            // Desktop (non-popup): stay in the same tab
            window.location.href = routeData.href
          }
          break
        default:
          await this.setTaskCurrentTaskType(task.taskType)
          if (isPopup) {
            window.opener.location.href = '/task'
            setTimeout(() => {
              window.close()
            }, 500)
          } else {
            await this.$router.push({ path: '/task' })
          }
          break
      }
    },
    async checkAndProcessRemainingTasks(queryData) {
      try {
        // Fetch remaining tasks after completing the current one
        const res = await api.patient.request.gettasks({
          facilityId: this.currentPatientAssociated.facilityID,
          patientId: queryData.pid
        })
        const taskList = res.data

        // Current task we just completed (avoid treating it as "remaining" if server hasn't updated yet)
        const currentTaskId = queryData.tid ? parseInt(queryData.tid, 10) : null

        // Filter out tasks with taskID === 0 (completed) and the task we just completed
        const remainingTasks = taskList.filter(
          (task) =>
            task.taskID > 0 &&
            (currentTaskId == null || task.taskID !== currentTaskId)
        )

        if (remainingTasks.length > 0) {
          this.transitionMessage = 'Loading next task…'
          // Try to give the UI a beat to paint the overlay before navigating away.
          await this.$nextTick()
          await new Promise((resolve) => setTimeout(resolve, 75))

          // Only load another FORM (signing) task — complete all forms before thank you.
          // Do NOT load NPH/CH (would send user to Patient Info); show thank you instead.
          const nextFormTask = remainingTasks.find(
            (task) => task.taskType === 'FORM'
          )
          if (nextFormTask) {
            await this.processNextTask(nextFormTask, queryData)
          } else {
            // No more FORM tasks — ensure any NPH/patient health history task is completed before redirect
            await this.ensureHealthHistoryTaskCompleted(queryData)
            if (this.survey) {
              this.survey.completedHtml =
                '<span style="font-size: 32px; font-weight: bolder">Thank you for completing the form</span>'
            }
            this.isTransitioning = false
            await new Promise(resolve => setTimeout(resolve, 750))
            this.handleFormCompletionRedirect()
          }
        } else {
          // No more tasks: ensure any NPH/patient health history task is completed, then redirect
          await this.ensureHealthHistoryTaskCompleted(queryData)
          if (this.survey) {
            this.survey.completedHtml =
              '<span style="font-size: 32px; font-weight: bolder">Thank you for completing the form</span>'
          }
          this.isTransitioning = false
          // Then proceed with normal redirect.
          await new Promise((resolve) => setTimeout(resolve, 750))
          this.handleFormCompletionRedirect()
        }
      } catch (error) {
        console.error('Error checking remaining tasks:', error)
        // On error, proceed with normal redirect (avoid showing "Thank you" prematurely).
        this.isTransitioning = false
        this.handleFormCompletionRedirect()
      }
    },
    /**
     * After the last form signing step, ensure any remaining patient health history task (NPH, AANPH, AACH, CH)
     * is marked complete so the NPH/patient form task is completed when there is no succeeding form task.
     */
    async ensureHealthHistoryTaskCompleted(queryData) {
      const facilityId = this.currentPatientAssociated?.facilityID
      const patientId = queryData?.pid ?? this.currentPatientAssociated?.patientID
      if (!facilityId || !patientId) return

      try {
        const res = await api.patient.request.gettasks({ facilityId, patientId })
        const raw = res.data
        const list = Array.isArray(raw)
          ? raw
          : raw?.tasks && Array.isArray(raw.tasks)
            ? raw.tasks
            : []
        const healthHistoryTypes = ['NPH', 'AANPH', 'AACH', 'CH']
        const incompleteHealthHistoryTasks = list.filter(
          (t) =>
            t.taskID != null &&
            Number(t.taskID) > 0 &&
            healthHistoryTypes.includes(t.taskType)
        )

        for (const task of incompleteHealthHistoryTasks) {
          const taskId = Number(task.taskID)
          if (Number.isNaN(taskId)) continue
          try {
            await api.task.request.updateTaskComplete({
              facilityId,
              id: taskId,
              patientId
            })
          } catch (e) {
            console.warn(
              'HandlerPage: ensureHealthHistoryTaskCompleted updateTaskComplete failed for task',
              taskId,
              e
            )
          }
        }
      } catch (e) {
        console.warn('HandlerPage: ensureHealthHistoryTaskCompleted gettasks failed', e)
      }
    },
    handleFormCompletionRedirect() {
      var isMobileDevice = window.navigator.userAgent
        .toLowerCase()
        .includes('mobi')
      const isPopup = !isMobileDevice && window.opener

      if (isPopup) {
        // For popup windows, redirect opener to home and close popup
        if (window.opener) {
          window.opener.location.href = '/'
          setTimeout(() => {
            window.close()
          }, 500)
        } else {
          // If opener is closed, redirect current window
          setTimeout(() => {
            this.$router.push('/')
          }, 500)
        }
      } else {
        // For regular windows (mobile or desktop), redirect to home page
        setTimeout(() => {
          this.$router.push('/')
        }, 500)
      }
    }
  },
  async mounted() {
    try {
      const x = await this.loadData()
      if (!x) {
        console.error('Failed to load survey data')
        this.isLoading = false
        return
      }
      this.survey = new Model(x)
      this.survey.onCompleting.add(this.complete)
      this.survey.addNavigationItem({
        id: 'pdf-export',
        title: 'Save as PDF',
        action: () => this.savePdf2(this.survey)
      })
      this.survey.onUpdateQuestionCssClasses.add(function (_, options) {
        if (options.question.name === 'patientTypedSignature') {
          options.cssClasses.root += ' scriptedSignatureInput'
        }
      })
    } catch (error) {
      console.error('Error initializing survey:', error)
    } finally {
      this.isLoading = false
    }
  }
}
</script>

<style>
@import url('https://fonts.googleapis.com/css2?family=Great+Vibes&display=swap');

.scriptedSignatureInput {
  font-family: 'Great Vibes', cursive !important;
  font-size: 18px !important;
  font-weight: 550;
}

.sv-signaturepad {
  border: 1px solid gray !important;
}

.sd-signaturepad {
  border: 1px solid gray !important;
}

@media (min-width: 449px) and (max-width: 495px) {
  .sd-title.sd-container-modern__title .sd-logo__image {
    width: 25rem;
  }
}

@media (min-width: 496px) and (max-width: 497px) {
  .sd-title.sd-container-modern__title .sd-logo__image {
    width: 28rem;
  }
}

.loading-message {
  text-align: center;
  padding: 20px;
  font-size: 16px;
  color: #666;
}

.task-transition-overlay {
  position: fixed;
  inset: 0;
  z-index: 9999;
  background: rgba(255, 255, 255, 0.88);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
}

.task-transition-card {
  width: min(520px, 100%);
  border-radius: 12px;
  background: #fff;
  border: 1px solid rgba(0, 0, 0, 0.08);
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.12);
  padding: 18px 20px;
}

.task-transition-title {
  font-size: 18px;
  font-weight: 600;
  color: #111;
  text-align: center;
}
</style>
